/* Virtual browser-history entries for in-page layers (a drilled-down map, a
   popup, a modal stacked on that popup). Each push adds a real history entry
   whose URL is unchanged, so vue-router resolves the identical route and stays
   put while the browser Back button consumes our entry instead of leaving the
   page.

   Module-level rather than per-instance on purpose: the layers live in
   different components (the map owns its drill-down, the page owns its popup)
   but share one chronological stack, which is exactly what Back must walk. */

const STATE_KEY = 'in_page_step';

const steps = [];
let is_listening = false;

function readDepth(state){
  return (state && state[STATE_KEY]) || 0;
}

function handlePopState(event){
  const targetDepth = readDepth(event.state);
  /* A leftover entry from a stack whose components have since unmounted (the
     guest left the page with a layer open and came back). Skip straight past
     it, or Back would feel dead for as many presses as there were layers. */
  if(targetDepth > steps.length){
    window.history.back();
    return;
  }
  while(steps.length > targetDepth){
    const step = steps.pop();
    if(!step.isReleased) step.onPop();
  }
}

/**
 * Adds one virtual history entry backed by onPop, called when the browser's
 * Back button consumes it.
 * @param {Function} onPop closes the layer; the history entry is already gone
 * @returns {Object} handle to pass to releaseHistoryStep
 */
export function pushHistoryStep(onPop){
  if(!is_listening){
    window.addEventListener('popstate', handlePopState);
    is_listening = true;
  }
  const step = { onPop: onPop, isReleased: false };
  steps.push(step);
  // copies vue-router's own key so its saved scroll position survives
  const state = Object.assign({}, window.history.state);
  state[STATE_KEY] = steps.length;
  window.history.pushState(state, '', window.location.href);
  return step;
}

/**
 * The UI closed the layer itself (close button, breadcrumb, Escape), so rewind
 * the browser past its entry. Layers stacked ABOVE it are not marked released,
 * so their onPop still runs and they close along with it.
 * @param {Object} step handle returned by pushHistoryStep
 * @returns {void}
 */
export function releaseHistoryStep(step){
  const index = steps.indexOf(step);
  if(index < 0) return;
  step.isReleased = true;
  window.history.go(-(steps.length - index));
}

/**
 * Drops the stack without touching history, for beforeDestroy: rewinding there
 * would fight a router navigation already in flight. The stale entries left
 * behind are skipped by handlePopState.
 * @returns {void}
 */
export function abandonHistorySteps(){
  steps.length = 0;
}



// WEBPACK FOOTER //
// ./src/utils/historyStep.js